/* eslint-disable */
import { useRef, useMemo } from "react";
import JoditEditor from "jodit-react";

import { uploadSingleFile } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const JoditEditorField = ({
  label,
  value,
  onChange,
  labelClassName = "",
  editorHeight = null,
  required = false,
}) => {
  const token = localStorage.getItem("token");
  const editorRef = useRef(null);       // jodit-react ref (textarea DOM node in this version)
  const joditInstanceRef = useRef(null); // actual Jodit editor instance, set via afterInit

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "",
      minHeight: editorHeight || 300,
      toolbarSticky: false,
      uploader: {
        insertImageAsBase64URI: false,
        url: `${url.API_BASE_URL}${url.API_SINGLE_FILE_UPLOAD}`,
        format: "json",
        method: "POST",
        // Match the field name used by the server ("upload")
        filesVariableName: () => "upload",
        isSuccess: (resp) => resp.status,
        getMessage: (resp) => resp.message || "Upload failed",
        process: (resp) => ({
          files: [resp.data.path],
          path: "",
          baseurl: url.SERVER_URL,
          error: resp.status ? 0 : 1,
          msg: resp.message || "",
        }),
        defaultHandlerSuccess: function (data) {
          const { files, baseurl } = data;
          if (!files || !files.length) return;
          // Use joditInstanceRef which holds the real Jodit instance from afterInit
          const editor = joditInstanceRef.current;
          if (editor && editor.selection) {
            files.forEach((file) => {
              editor.selection.insertImage(baseurl + file);
            });
          }
        },
      },
      defaultActionOnPaste: "insert_as_html",
      defaultLineHeight: 1.5,
      enter: "div",
      statusbar: false,
      sizeLG: 900,
      sizeMD: 700,
      sizeSM: 400,
      toolbarAdaptive: false,
      disablePlugins: [
        "video",
        "about",
        "classSpan",
        "file",
        "speechRecognize",
        "copyformat",
        "spellcheck",
        "add-new-line",
      ],
      events: {
        // Capture the real Jodit instance as soon as the editor initialises
        afterInit: (editor) => {
          joditInstanceRef.current = editor;
        },
        afterPaste: async (event) => {
          const clipboardData = event.clipboardData || window.clipboardData;
          const items = clipboardData.items;

          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
              const file = items[i].getAsFile();
              if (!file) continue;

              // Rename file with timestamp
              const timestamp = Date.now();
              const fileExtension = file.type.split("/")[1];
              const newFileName = `image-${timestamp}.${fileExtension}`;
              const renamedFile = new File([file], newFileName, {
                type: file.type,
              });

              try {
                const requestUrl = `${url.API_BASE_URL}${url.API_SINGLE_FILE_UPLOAD}`;
                const response = await uploadSingleFile(requestUrl, renamedFile);

                if (response.status) {
                  const imageUrl = url.SERVER_URL + response.data.path;
                  const editorInstance = event.target;
                  const content = editorInstance.value;

                  // Replace base64 images with server URLs
                  const regex = /<img[^>]+src="data:image\/[^">]+"/g;
                  const newContent = content.replace(regex, (match) => {
                    const base64DataMatch = match.match(/src="([^"]+)"/);
                    if (
                      base64DataMatch &&
                      base64DataMatch[1].startsWith("data:image/")
                    ) {
                      return match.replace(base64DataMatch[1], imageUrl);
                    }
                    return match;
                  });

                  editorInstance.value = newContent;
                  editorInstance.events.fire("change", newContent);
                }
              } catch (error) {
                console.error("Image upload failed:", error);
              }
            }
          }
        },
      },
    }),
    [token],
  );

  return (
    <div className="form-group mb-4">
      <label className={labelClassName}>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      <JoditEditor
        ref={editorRef}
        value={value}
        config={editorConfig}
        onBlur={onChange}
      />
    </div>
  );
};

export default JoditEditorField;
